import { EllipsisIcon, Trash2Icon } from 'lucide-react';
import { useNavigate } from 'react-router';

import { WorkflowDTO } from '@invenco/common-interface/automation';

import {
  AlertDialogWithAction,
  AlertDialogWithActionCancel,
  AlertDialogWithActionContent,
  AlertDialogWithActionDescription,
  AlertDialogWithActionFooter,
  AlertDialogWithActionHeader,
  AlertDialogWithActionSubmit,
  AlertDialogWithActionTitle,
  AlertDialogWithActionTrigger,
} from '@/components/alert-dialog-with-action';
import { Button } from '@/components/core/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/core/dropdown-menu';
import { HeaderActions } from '@/components/header';
import { Result } from '@/shared/helpers/Result';

import { WorkflowEditorState } from './use-workflow-editor-state';

type Props = {
  editor: WorkflowEditorState;
  /** Undefined while loading and always in create mode. */
  workflow?: WorkflowDTO;
  isNew: boolean;
  loading?: boolean;
  deleteWorkflow: () => Promise<Result>;
};

/**
 * Header actions: the staged Save and, in edit mode, a more-actions menu
 * holding Delete behind a confirmation that returns to the list.
 */
export function WorkflowActions({ editor, workflow, isNew, loading, deleteWorkflow }: Props) {
  const navigate = useNavigate();

  const onDelete = async (): Promise<Result> => {
    const result = await deleteWorkflow();
    if (result.isSuccess) {
      await navigate('/automation/workflows');
    }
    return result;
  };

  return (
    <HeaderActions>
      {isNew ? null : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label="More actions"
              disabled={loading || !workflow}
            >
              <EllipsisIcon />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <AlertDialogWithAction onAction={onDelete}>
              <AlertDialogWithActionTrigger asChild>
                {/* preventDefault keeps the menu mounted so the dialog it hosts can open */}
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={(event) => event.preventDefault()}
                >
                  <Trash2Icon />
                  Delete
                </DropdownMenuItem>
              </AlertDialogWithActionTrigger>
              <AlertDialogWithActionContent>
                <AlertDialogWithActionHeader>
                  <AlertDialogWithActionTitle>Delete this automation?</AlertDialogWithActionTitle>
                  <AlertDialogWithActionDescription>
                    This action cannot be undone.
                  </AlertDialogWithActionDescription>
                </AlertDialogWithActionHeader>
                <AlertDialogWithActionFooter>
                  <AlertDialogWithActionCancel>Cancel</AlertDialogWithActionCancel>
                  <AlertDialogWithActionSubmit>Delete</AlertDialogWithActionSubmit>
                </AlertDialogWithActionFooter>
              </AlertDialogWithActionContent>
            </AlertDialogWithAction>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <Button type="button" onClick={editor.submit} loading={editor.isSaving} disabled={loading}>
        {isNew ? 'Create workflow' : 'Save changes'}
      </Button>
    </HeaderActions>
  );
}
